import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, Paper
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { UnitOfMeasurement, UnitOfMeasurementCreateDTO } from '../../interfaces/unit-of-measurement.interface';
import { UnitOfMeasurementService } from '../../services/unitOfMeasurement.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const UnitOfMeasurementList: React.FC = () => {
    const toast = useToast();
    const [units, setUnits] = useState<UnitOfMeasurement[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUnit, setEditingUnit] = useState<UnitOfMeasurement | null>(null);
    const [formData, setFormData] = useState<UnitOfMeasurementCreateDTO>({
        name: '', symbol: '', description: ''
    });

    const institutionId = AuthService.getSelectedInstitution();

    // Global (cross-institution) units are a SystemAdmin-only platform concept, not an
    // institution permission — keep reading the JWT role here.
    const currentUserRoles = AuthService.getCurrentUser()?.roles;
    const isSystemAdmin = (Array.isArray(currentUserRoles) ? currentUserRoles : [currentUserRoles]).includes('SystemAdmin');
    const canManage = usePermission(PERMISSIONS.UOM_MANAGE);

    const fetchUnits = async () => {
        try {
            setLoading(true);
            const data = await UnitOfMeasurementService.getAll();
            setUnits(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load units');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUnits(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const canEdit = (unit: UnitOfMeasurement) => {
        if (unit.isGlobal) return isSystemAdmin;
        return isSystemAdmin || unit.institutionId === institutionId;
    };

    const handleOpenDialog = (unit?: UnitOfMeasurement) => {
        if (unit) {
            setEditingUnit(unit);
            setFormData({ name: unit.name, symbol: unit.symbol, description: unit.description });
        } else {
            setEditingUnit(null);
            setFormData({ name: '', symbol: '', description: '' });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingUnit(null);
        setFormData({ name: '', symbol: '', description: '' });
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.symbol) {
            toast.showError('Name and symbol are required');
            return;
        }
        try {
            const dto: UnitOfMeasurementCreateDTO = {
                ...formData,
                institutionId: isSystemAdmin ? undefined : (institutionId || undefined),
            };
            if (editingUnit) {
                await UnitOfMeasurementService.update(editingUnit.id, dto);
                toast.showSuccess('Unit updated');
            } else {
                await UnitOfMeasurementService.create(dto);
                toast.showSuccess('Unit created');
            }
            await fetchUnits();
            handleCloseDialog();
        } catch (err) {
            toast.showApiError(err, 'Failed to save unit');
        }
    };

    const handleDelete = async (unit: UnitOfMeasurement) => {
        try {
            await UnitOfMeasurementService.delete(unit.id);
            toast.showSuccess('Unit deleted');
            await fetchUnits();
        } catch (err) {
            toast.showApiError(err, 'Failed to delete unit — it may be in use by items');
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div style={{ padding: '20px' }} data-help-page="store.units.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h4">Units of Measurement</Typography>
                {(isSystemAdmin || canManage) && (
                    <Button data-help="store.units.add" variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                        Add Unit
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper} data-help-section="store.units.table">
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell data-help="store.units.col.name">Name</TableCell>
                            <TableCell data-help="store.units.col.symbol">Symbol</TableCell>
                            <TableCell data-help="store.units.col.description">Description</TableCell>
                            <TableCell data-help="store.units.col.type">Type</TableCell>
                            <TableCell data-help="store.units.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {units.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No units found</TableCell>
                            </TableRow>
                        ) : (
                            units.map(unit => (
                                <TableRow key={unit.id}>
                                    <TableCell>{unit.name}</TableCell>
                                    <TableCell><strong>{unit.symbol}</strong></TableCell>
                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{unit.description || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={unit.isGlobal ? 'Global' : 'Custom'}
                                            color={unit.isGlobal ? 'primary' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={canEdit(unit) ? 'Edit' : 'Global units can only be edited by SystemAdmin'}>
                                            <span>
                                                <IconButton data-help="store.units.action.edit" size="small" color="primary" disabled={!canEdit(unit)} onClick={() => handleOpenDialog(unit)}>
                                                    <Edit />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={canEdit(unit) ? 'Delete' : 'Global units can only be deleted by SystemAdmin'}>
                                            <span>
                                                <IconButton data-help="store.units.action.delete" size="small" color="error" disabled={!canEdit(unit)} onClick={() => handleDelete(unit)}>
                                                    <Delete />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog data-help-page="store.unitForm.page" open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingUnit ? 'Edit Unit' : 'Add Unit'}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth data-help="store.unitForm.field.name" label="Name" value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required margin="normal"
                    />
                    <TextField
                        fullWidth data-help="store.unitForm.field.symbol" label="Symbol (e.g. pc, L, kg)" value={formData.symbol}
                        onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                        required margin="normal"
                    />
                    <TextField
                        fullWidth data-help="store.unitForm.field.description" label="Description (optional)" value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="store.unitForm.cancel" onClick={handleCloseDialog}>Cancel</Button>
                    <Button data-help="store.unitForm.save" variant="contained" onClick={handleSubmit}>
                        {editingUnit ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default UnitOfMeasurementList;
